import React from 'react';
import { LightbulbIcon } from '@bizfirst/common-react';
import { ExecutionObserverConfig } from '../../models/ExecutionObserverConfig';
import '../../styles/tabs/PerformanceFiltersTab.css';

interface PerformanceFiltersTabProps {
  config: ExecutionObserverConfig;
  onChange: (config: ExecutionObserverConfig) => void;
  readonly?: boolean;
}

export const PerformanceFiltersTab: React.FC<PerformanceFiltersTabProps> = ({
  config,
  onChange,
  readonly = false
}) => {
  const filter = config.filter || {};
  const perf = filter.performance || {};

  const updatePerformance = (updates: any) => {
    onChange({
      ...config,
      filter: {
        ...filter,
        performance: { ...perf, ...updates }
      }
    });
  };

  return (
    <fieldset className="performance-filters-tab" disabled={readonly}>
      <legend>Performance Filters (milliseconds)</legend>
      <small className="help-text" style={{ display: 'block', marginTop: '10px' }}>Leave fields blank to disable thresholds</small>

      <div className="form-group" style={{ marginTop: '12px' }}>
        <label htmlFor="min-duration">Minimum Duration (ms):</label>
        <input
          id="min-duration"
          type="number"
          min="0"
          step="10"
          placeholder="0"
          value={perf.minDurationMs ?? ''}
          onChange={(e) =>
            updatePerformance({
              minDurationMs: e.target.value ? parseInt(e.target.value) : undefined
            })
          }
          disabled={readonly}
        />
        <small className="help-text">
          Only show events lasting at least this long
        </small>
      </div>

      <div className="form-group">
        <label htmlFor="max-duration">Maximum Duration (ms):</label>
        <input
          id="max-duration"
          type="number"
          min="0"
          step="10"
          placeholder="0"
          value={perf.maxDurationMs ?? ''}
          onChange={(e) =>
            updatePerformance({
              maxDurationMs: e.target.value ? parseInt(e.target.value) : undefined
            })
          }
          disabled={readonly}
        />
        <small className="help-text">
          Only show events shorter than this duration
        </small>
      </div>

      <div className="form-group">
        <label htmlFor="slow-threshold">Slow Operation Threshold (ms):</label>
        <input
          id="slow-threshold"
          type="number"
          min="0"
          step="100"
          placeholder="1000"
          value={perf.slowThresholdMs ?? ''}
          onChange={(e) =>
            updatePerformance({
              slowThresholdMs: e.target.value ? parseInt(e.target.value) : undefined
            })
          }
          disabled={readonly}
        />
        <small className="help-text">
          Flag events slower than this threshold
        </small>
      </div>

      <div className="duration-example">
        <strong>Common Thresholds:</strong>
        <ul>
          <li>Fast operations: 0-100ms</li>
          <li>Normal operations: 100-1000ms</li>
          <li>Slow operations: 1000-5000ms</li>
          <li>Very slow operations: &gt;5000ms</li>
        </ul>
      </div>

      <div className="info-box">
        <p>
          <LightbulbIcon size={14} /> <strong>Tip:</strong> Use performance filters to find bottlenecks and slow operations.
        </p>
      </div>
    </fieldset>
  );
};

export default PerformanceFiltersTab;
